import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { unwrap, apiError } from "@/lib/api";
import { EP } from "@/lib/endpoints";
import PostCard from "@/components/PostCard";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft,
  Loader2,
  Heart,
  Trash2,
  Sparkles,
  MessageCircle,
  Send,
  MoreHorizontal,
  Flag,
  UserX,
} from "lucide-react";
import { toast } from "sonner";
import UserAvatar from "@/components/UserAvatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ReportModal from "@/components/ReportModal";
import ModerationWarning from "@/components/ModerationWarning";
import {
  isModerationFlagged,
  isModerationError,
} from "@/lib/moderation";
import { useAuthor, primeAuthor } from "@/lib/authors";
import { useSeo } from "@/lib/useSeo";

function relTime(input) {
  if (!input) return "";
  const d = new Date(input);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (Number.isNaN(diff)) return "";
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function Reply({
  reply,
  isMine,
  onDelete,
  onBlockedUser,
  postAuthorId,
  postAuthor,
  postId,
  currentUser,
}) {
  const [r, setR] = useState(reply);
  const [reportOpen, setReportOpen] = useState(false);
  const rid = r.id ?? r.reply_id;
  // Xano's /replies response does not inline `author` — only `user_id`.
  // Prefer the inline record when it exists; otherwise hydrate via the
  // shared useAuthor cache so subsequent rows for the same user don't
  // refetch.  Falls back to `currentUser` / `postAuthor` when the reply
  // is by the viewer or the post author.
  const inlineAuthor = r.author || r.user;
  // Seed the cache when we already have a rich inline record so other
  // rows / DVs referencing the same user skip the fetch.
  useEffect(() => {
    if (inlineAuthor) primeAuthor(inlineAuthor);
  }, [inlineAuthor]);
  const selfMatch = currentUser && r.user_id === currentUser.id;
  const postAuthorMatch =
    postAuthorId && postAuthor && r.user_id === postAuthorId;
  const seedForFetch = selfMatch
    ? currentUser
    : postAuthorMatch
    ? postAuthor
    : inlineAuthor || undefined;
  const hydrated = useAuthor(r.user_id, seedForFetch);
  const author = inlineAuthor || hydrated;
  const agreed = !!r.viewer_has_agreed || !!r.i_agreed;
  const agreeCount = r.agree_count ?? r.likes_count ?? 0;

  const toggleAgree = async () => {
    setR((prev) => ({
      ...prev,
      viewer_has_agreed: !agreed,
      agree_count: (prev.agree_count ?? agreeCount) + (agreed ? -1 : 1),
    }));
    try {
      if (agreed) await api.delete(EP.agreeReply(rid));
      else await api.post(EP.agreeReply(rid));
    } catch (e) {
      setR((prev) => ({
        ...prev,
        viewer_has_agreed: agreed,
        agree_count: (prev.agree_count ?? agreeCount) + (agreed ? 1 : -1),
      }));
      toast.error(apiError(e));
    }
  };

  const blockAuthor = async () => {
    const uid = r.user_id ?? author?.user_id ?? author?.id;
    if (uid == null && !author?.username) {
      toast.error("Couldn't identify this user.");
      return;
    }
    if (!window.confirm(`Block @${author.username}?`)) return;
    try {
      // iOS spec: POST /block/{user_id} REQUIRES { username } body.
      await api.post(EP.blockUser(uid), { username: author?.username });
      toast.success(`@${author.username} blocked`);
      onBlockedUser?.(uid, author?.username);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div
      className="rounded-[20px] border border-border bg-card p-4"
      data-testid={`reply-${rid}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Link to={`/${author.username}`}>
          <UserAvatar user={author} size="sm" />
        </Link>
        <div className="flex-1 min-w-0">
          <Link
            to={`/${author.username}`}
            className="text-foreground text-[14px] font-bold hover:underline underline-offset-4 truncate block"
          >
            {author.display_name || author.username}
          </Link>
          <div className="text-muted-foreground text-[12px] truncate">
            @{author.username}
            {r.created_at ? ` · ${relTime(r.created_at)}` : ""}
          </div>
        </div>
        {isMine ? (
          <button
            onClick={() => onDelete(rid)}
            className="text-muted-foreground hover:text-destructive p-1"
            aria-label="Delete reply"
            data-testid={`reply-delete-${rid}`}
          >
            <Trash2 size={16} strokeWidth={1.6} />
          </button>
        ) : (
          // Three-dot menu — "Report comment" + destructive "Block user".
          // Matches iOS spec 13 §Reply exactly.
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="pl-2 -mr-1 text-muted-foreground hover:opacity-80"
                aria-label="More"
                data-testid={`reply-menu-${rid}`}
              >
                <MoreHorizontal size={18} strokeWidth={1.8} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => setReportOpen(true)}
                data-testid={`reply-menu-report-${rid}`}
              >
                <Flag className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Report comment
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={blockAuthor}
                className="text-destructive focus:text-destructive"
                data-testid={`reply-menu-block-${rid}`}
              >
                <UserX className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Block user
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <p className="text-foreground text-[14.5px] leading-5 whitespace-pre-wrap break-words">
        {r.content}
      </p>
      <button
        onClick={toggleAgree}
        className="mt-2.5 inline-flex items-center gap-1.5 transition-transform active:scale-95"
        data-testid={`reply-agree-${rid}`}
      >
        <Heart
          size={15}
          strokeWidth={1.9}
          className={agreed ? "" : "text-muted-foreground"}
          style={
            agreed
              ? { color: "#F91880", fill: "#F91880" }
              : { fill: "transparent" }
          }
        />
        {(r.agree_count ?? agreeCount) ? (
          <span
            className="text-[12px] font-bold tabular-nums"
            style={{
              color: agreed ? "#F91880" : "hsl(var(--muted-foreground))",
            }}
          >
            {r.agree_count ?? agreeCount}
          </span>
        ) : null}
      </button>

      <ReportModal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        targetType="reply"
        targetId={rid}
        targetLabel={r.content}
      />
    </div>
  );
}

// -------------------------------------------------------------------
// Xano returns grouped/joined columns as 1-element arrays (a
// group_concat quirk). This helper unwraps `{id:[2], content:["hi"]}` →
// `{id:2, content:"hi"}` for a cleaner render surface.
// -------------------------------------------------------------------
function unwrapGrouped(item) {
  if (!item || typeof item !== "object") return item;
  const out = {};
  for (const [k, v] of Object.entries(item)) {
    out[k] = Array.isArray(v) && v.length === 1 ? v[0] : v;
  }
  return out;
}

/**
 * PublicDifferentViews — the "different-view" comments are now public
 * (backend change: /different-view/{post_id} POST returns the record and
 * the parent GET /posts/{id} inlines them under `interactions.different_views`).
 * Everyone can see them; no owner gate.
 */
function PublicDifferentViews({ items = [], postAuthor, currentUser, onDelete }) {
  if (!items || items.length === 0) return null;
  const unwrapped = items.map(unwrapGrouped);
  return (
    <div className="mt-6 relative rounded-3xl border border-border p-6 overflow-hidden" data-testid="dv-public-list">
      <div className="absolute left-0 top-0 bottom-0 w-1 rainbow-bg-v" />
      <div className="pl-2">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-3">
          <MessageCircle className="w-3 h-3" strokeWidth={2} />
          Different views
        </div>
        <h3 className="font-display text-xl font-medium tracking-tight mb-1">
          Voices that see this <span className="rainbow-text">differently</span>
        </h3>
        <p className="text-xs text-muted-foreground mb-4">
          {unwrapped.length} {unwrapped.length === 1 ? "person" : "people"} shared a different perspective.
        </p>
        <div className="space-y-3">
          {unwrapped.map((dv, i) => (
            <DifferentViewItem
              key={dv.id ?? i}
              item={dv}
              postAuthor={postAuthor}
              currentUser={currentUser}
              onDelete={onDelete}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function DifferentViewItem({ item, postAuthor, currentUser, onDelete }) {
  // Author resolution for public DV / agree-comment rows.
  //   1. Prefer any inline author record embedded on the item — the
  //      backend sometimes ships `author` / `user` / `_user` / `profile`.
  //   2. If the row is the viewer's own, use `currentUser` (has avatar).
  //   3. If the row is the post's own author, reuse `postAuthor`.
  //   4. Otherwise hydrate the identity + avatar via `useAuthor(user_id)`
  //      — one `GET /users/{id}` per user per session, cached module-wide
  //      so multiple DV rows for the same person share a single request.
  const inlineAuthor =
    item.author ||
    item.user ||
    item._user ||
    item.profile ||
    item._profile ||
    null;
  const isPostAuthor =
    postAuthor &&
    (postAuthor.id === item.user_id || postAuthor.user_id === item.user_id);
  const isMine = currentUser && item.user_id === currentUser.id;
  const seed = isMine
    ? currentUser
    : isPostAuthor
      ? postAuthor
      : inlineAuthor;
  // Skip the fetch when we already have a rich seed (self / post
  // author / inline author) — useAuthor is a no-op in that case.
  const hydrated = useAuthor(item.user_id, seed);
  const author = inlineAuthor || hydrated;
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (deleting) return;
    if (!window.confirm("Delete your different view?")) return;
    setDeleting(true);
    try {
      await api.delete(EP.deleteDifferentView(item.id));
      onDelete?.(item.id);
      toast.success("Different view removed.");
    } catch (e) {
      toast.error(apiError(e));
      setDeleting(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border p-4" data-testid={`dv-item-${item.id ?? "x"}`}>
      <div className="flex items-center gap-2 mb-2">
        <UserAvatar user={author} size="sm" />
        <div className="text-xs min-w-0">
          <div className="text-sm font-medium truncate">{author.display_name || author.username}</div>
          <div className="text-[11px] text-muted-foreground truncate">@{author.username}</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {item.created_at && (
            <span className="text-[11px] text-muted-foreground">{relTime(item.created_at)}</span>
          )}
          {isMine && onDelete && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="w-7 h-7 rounded-full hover:bg-destructive/10 text-muted-foreground hover:text-destructive inline-flex items-center justify-center disabled:opacity-40"
              data-testid={`dv-delete-${item.id}`}
              aria-label="Delete your different view"
              title="Delete your different view"
            >
              {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />}
            </button>
          )}
        </div>
      </div>
      <p className="text-sm text-foreground/95 whitespace-pre-wrap">{item.content}</p>
    </div>
  );
}

/**
 * PublicAgreeComments — Xano now stores optional public "agree comments"
 * alongside each Agree vote (backend embeds them under
 * `interactions.agree_comments`). Shown for opinion posts.
 */
function PublicAgreeComments({ items = [], postAuthor }) {
  if (!items || items.length === 0) return null;
  const unwrapped = items.map(unwrapGrouped);
  return (
    <div className="mt-6 rounded-3xl border border-emerald-500/30 bg-emerald-500/[0.03] p-6" data-testid="agree-public-list">
      <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-emerald-600 mb-3">
        <Sparkles className="w-3 h-3" strokeWidth={2} />
        Agreed with a comment
      </div>
      <div className="space-y-3">
        {unwrapped.map((c, i) => (
          <DifferentViewItem key={c.id ?? i} item={c} postAuthor={postAuthor} />
        ))}
      </div>
    </div>
  );
}

export default function PostDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  // Moderation flag — true when the backend rejects a reply for violating
  // Community Guidelines (either a flagged 200 payload or a 4xx error).
  const [replyFlagged, setReplyFlagged] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const [pRes, rRes] = await Promise.all([
        api.get(EP.post(id)),
        api.get(EP.replies(id)),
      ]);
      const p = unwrap(pRes.data);
      setPost(p?.post || p);
      const r = unwrap(rRes.data);
      const list = Array.isArray(r) ? r : (r?.items || r?.replies || r?.data || []);
      setReplies(list);
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Per-post SEO — Google + social crawlers key the URL to the poster
  // and a snippet of the post body. Bare `/post/:id` in every share
  // preview would collapse into a single duplicate result across every
  // post, so the title has to include the author handle + a summary.
  const postAuthorUname = post?.author?.username || post?.user?.username;
  const postAuthorName = post?.author?.display_name || postAuthorUname;
  const postSummary = (() => {
    const raw = (post?.content || post?.text || post?.title || "").trim();
    if (!raw) return "";
    const clean = raw.replace(/\s+/g, " ");
    return clean.length > 180 ? `${clean.slice(0, 177)}…` : clean;
  })();
  const postSeoTitle = postAuthorName
    ? `${postAuthorName} (@${postAuthorUname}): "${postSummary || "a post"}" • BrontoY`
    : "Post • BrontoY";
  const postSeoDescription =
    postSummary ||
    (postAuthorUname
      ? `A post by @${postAuthorUname} on BrontoY.`
      : "A post on BrontoY.");
  useSeo({
    title: postSeoTitle,
    description: postSeoDescription,
    canonicalPath: `/post/${id}`,
    ogType: "article",
  });

  const submitReply = async () => {
    if (!text.trim()) return;
    setPosting(true);
    setReplyFlagged(false);
    try {
      const res = await api.post(EP.createReply(id), { content: text.trim() });
      const d = unwrap(res.data);
      const newReply = d?.reply || d;
      // Moderation can flag on a 200 payload too — show the red warning
      // and keep the text so the user can edit it (per iOS spec 13).
      if (isModerationFlagged(d) || isModerationFlagged(newReply)) {
        setReplyFlagged(true);
        return;
      }
      setReplies((prev) => [newReply, ...prev]);
      setText("");
      toast.success("Reply posted.");
    } catch (e) {
      if (isModerationError(e)) setReplyFlagged(true);
      else toast.error(apiError(e));
    } finally {
      setPosting(false);
    }
  };

  const deleteReply = async (rid) => {
    if (!window.confirm("Delete this reply?")) return;
    try {
      await api.delete(EP.reply(rid));
      setReplies((prev) => prev.filter((r) => (r.id ?? r.reply_id) !== rid));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const rawPostType = post ? (post.post_type || post.Post_type || "") : "";
  const isOpinion = post && (
    rawPostType.toLowerCase() === "opinion" ||
    (post.type || "").toLowerCase() === "opinion" ||
    post.is_opinion === true
  );
  const postAuthor = post?.author || post?.user || {};

  // Xano now embeds public interactions on the post row itself:
  //   post.interactions.different_views  → public DV comments
  //   post.interactions.agree_comments   → public agree comments
  // We copy the DV array into local state so an optimistic delete on the
  // viewer's own DV row can drop it without waiting for a full reload.
  const [localDvs, setLocalDvs] = useState([]);
  useEffect(() => {
    setLocalDvs(post?.interactions?.different_views || []);
  }, [post]);
  const agreeCommentItems = post?.interactions?.agree_comments || [];

  const handleDeleteDv = useCallback((dvId) => {
    setLocalDvs((prev) => prev.filter((dv) => {
      const id = Array.isArray(dv.id) ? dv.id[0] : dv.id;
      return id !== dvId;
    }));
    // Refresh the parent post so counters stay in sync.
    api.get(EP.post(id)).then((res) => {
      const p = unwrap(res.data);
      setPost(p?.post || p);
    }).catch(() => { /* ignore — optimistic remove already applied */ });
  }, [id]);

  return (
    <div className="w-full md:max-w-none max-w-2xl mx-auto md:mx-0 px-4 sm:px-6 py-6">
      <Link to="/home" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="post-back">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>

      {loading ? (
        <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : err ? (
        <div className="rounded-3xl border border-destructive/40 bg-destructive/10 text-destructive p-6" data-testid="post-detail-error">{err}</div>
      ) : !post ? (
        <div className="rounded-3xl border border-border p-10 text-center text-muted-foreground">Post not found.</div>
      ) : (
        <>
          <PostCard post={post} inDetail />

          {/* Public interactions surfaces for Opinion posts */}
          {isOpinion && <PublicAgreeComments items={agreeCommentItems} postAuthor={postAuthor} currentUser={user} />}
          {isOpinion && (
            <PublicDifferentViews
              items={localDvs}
              postAuthor={postAuthor}
              currentUser={user}
              onDelete={handleDeleteDv}
            />
          )}

          {/* Reply composer + list — STANDARD posts only. Opinion posts use
              Agree + Different View instead (matches iOS spec 13). */}
          {!isOpinion && (
            <>
              {/* Replies list — 20-radius bordered card per row */}
              <div className="mt-4 flex flex-col gap-3">
                {replies.length === 0 ? (
                  <div className="rounded-[20px] border border-border bg-card py-8 text-center">
                    <span className="text-muted-foreground text-[14px]">
                      Be the first to reply.
                    </span>
                  </div>
                ) : (
                  replies.map((r) => {
                    const author = r.author || r.user || {};
                    const isMine =
                      user &&
                      (author.id === user.id ||
                        author.username === user.username ||
                        r.user_id === user.id);
                    const postAuthor = post?.author || post?.user;
                    const postAuthorId = post?.user_id || postAuthor?.id;
                    return (
                      <Reply
                        key={r.id ?? r.reply_id}
                        reply={r}
                        isMine={isMine}
                        onDelete={deleteReply}
                        currentUser={user}
                        postAuthorId={postAuthorId}
                        postAuthor={postAuthor}
                        postId={id}
                        onBlockedUser={(blockedUid) => {
                          // Remove every reply from the blocked user
                          // (spec 13: block invalidates the feed's view
                          // of their content).
                          setReplies((prev) =>
                            prev.filter((row) => row.user_id !== blockedUid)
                          );
                        }}
                      />
                    );
                  })
                )}
              </div>

              {/* Red Bronto moderation warning — shown when the backend
                  flags a reply for violating Community Guidelines.
                  Sits right above the composer per iOS spec 13. */}
              {replyFlagged && (
                <div className="mt-4 px-1">
                  <ModerationWarning testId="reply-moderation-warning" />
                </div>
              )}

              {/* Pinned composer — sticky at the bottom of the viewport
                  (iOS "padding-based avoidance": lives inside normal flow
                  on web, sticks so the reply pill stays reachable). */}
              <div
                className="sticky bottom-0 -mx-4 sm:-mx-6 mt-6 border-t border-border bg-background flex items-end gap-2.5 px-4 pt-3 pb-4"
                style={{ paddingBottom: "max(16px, env(safe-area-inset-bottom))" }}
              >
                <div className="shrink-0 pb-1">
                  <UserAvatar user={user} size="sm" />
                </div>
                <div className="flex-1 min-h-[40px] max-h-[112px] rounded-full border border-border bg-secondary px-4 py-2.5 flex items-center">
                  <textarea
                    value={text}
                    onChange={(e) => {
                      setText(e.target.value);
                      // Clear the flag as soon as the user edits, so the
                      // banner disappears on their next keystroke.
                      if (replyFlagged) setReplyFlagged(false);
                    }}
                    rows={1}
                    placeholder="Post your reply"
                    className="w-full bg-transparent text-foreground text-[15px] resize-none focus:outline-none placeholder:text-muted-foreground leading-snug"
                    style={{ maxHeight: 96 }}
                    data-testid="reply-textarea"
                  />
                </div>
                <button
                  type="button"
                  onClick={submitReply}
                  disabled={!text.trim() || posting}
                  className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-opacity disabled:opacity-40 active:opacity-85"
                  style={{ background: "hsl(var(--brand))" }}
                  data-testid="reply-submit"
                  aria-label="Post reply"
                >
                  {posting ? (
                    <Loader2 className="w-4 h-4 animate-spin text-white" />
                  ) : (
                    <Send size={16} className="text-white" />
                  )}
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
